import React, { useState } from "react";
import { Check, Truck, PackageCheck, ExternalLink, Send, Trash2, Store, Mail, Home } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  CARRIERS, buildTrackingUrl, carrierLabel, cleanTracking, orderStep, shortDate,
} from "../../lib/shipping";
import { effectiveQty, isShortItem } from "../../lib/orderMath";

const BTN_PRIMARY = "bg-[var(--terracotta)] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2 disabled:opacity-60";
const BTN_DARK = "bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-full px-4 py-2 text-sm font-medium hover:bg-[var(--ink-forest)]/90 transition-colors inline-flex items-center gap-2 disabled:opacity-60";
const BTN_SOFT = "bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-4 py-2 text-sm font-medium hover:border-[var(--ink-forest)]/30 transition-colors inline-flex items-center gap-2 disabled:opacity-60";
const INPUT = "w-full bg-[var(--bg-cream)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--ink-forest)] focus:outline-none focus:border-[var(--terracotta)]";

const STEP_CHIP = {
  unpaid: ["Awaiting payment", "bg-[var(--muted)] text-[var(--muted-fg)] border-[var(--border)]"],
  new: ["New", "bg-[var(--gold)]/15 text-[#8b6a2a] border-[var(--gold)]/40"],
  supplier: ["Waiting on supplier", "bg-[var(--ink-forest)]/8 text-[var(--ink-forest)] border-[var(--ink-forest)]/25"],
  partly: ["Partly shipped", "bg-[var(--terracotta)]/8 text-[var(--terracotta)] border-[var(--terracotta)]/30 border-dashed"],
  shipped: ["Shipped", "bg-[var(--terracotta)]/12 text-[var(--terracotta)] border-[var(--terracotta)]/35"],
  delivered: ["Delivered", "bg-[var(--ink-forest)] text-[var(--bg-cream)] border-[var(--ink-forest)]"],
  cancelled: ["Cancelled", "bg-[var(--muted)] text-[var(--muted-fg)] border-[var(--border)]"],
  expired: ["Expired", "bg-[var(--muted)] text-[var(--muted-fg)] border-[var(--border)]"],
  refunded: ["Refunded", "bg-[var(--muted)] text-[var(--muted-fg)] border-[var(--border)]"],
};

const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export function StepChip({ order, size = "sm" }) {
  const step = orderStep(order);
  const [label, classes] = STEP_CHIP[step] || STEP_CHIP.new;
  const sz = size === "sm" ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1";
  return (
    <span className={`inline-block shrink-0 ${sz} border rounded-full font-medium tracking-wide whitespace-nowrap ${classes}`} data-testid={`step-chip-${order.id}`}>
      {label}
    </span>
  );
}

function StepPath({ order }) {
  const step = orderStep(order);
  const shippedish = ["partly", "shipped", "delivered"].includes(step);
  const nodes = [
    { id: "new", label: "New", sub: shortDate(order.created_at), done: true },
    { id: "supplier", label: "Ordered from supplier", short: "From supplier", sub: order.supplier_ordered_at ? shortDate(order.supplier_ordered_at) : "optional", done: !!order.supplier_ordered_at, optional: true },
    { id: "shipped", label: step === "partly" ? "Partly shipped" : "Shipped", sub: order.shipped_at ? shortDate(order.shipped_at) : "", done: shippedish, partial: step === "partly" },
    { id: "delivered", label: "Delivered", sub: order.delivered_at ? shortDate(order.delivered_at) : "", done: step === "delivered" },
  ];
  const nextId = nodes.find((n) => (!n.done || n.partial) && !n.optional)?.id;
  return (
    <ol className="flex items-start" data-testid="order-step-path">
      {nodes.map((n, i) => (
        <li key={n.id} className="flex-1 flex flex-col items-center text-center relative" data-testid={`step-${n.id}-${n.done ? "done" : "todo"}`}>
          {i > 0 && (
            <span className={`absolute top-3.5 right-1/2 w-full h-0.5 z-0 ${nodes[i].done ? "bg-[var(--terracotta)]" : n.optional ? "border-t-2 border-dashed border-[var(--border)]" : "bg-[var(--border)]"}`} aria-hidden />
          )}
          <span
            className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
              n.done && !n.partial ? "bg-[var(--terracotta)] border-[var(--terracotta)] text-white"
                : n.partial ? "bg-[var(--bg-cream)] border-[var(--terracotta)] text-[var(--terracotta)] ring-4 ring-[var(--terracotta)]/15"
                : n.id === nextId ? "bg-[var(--bg-cream)] border-[var(--ink-forest)] ring-4 ring-[var(--ink-forest)]/10"
                : `bg-[var(--bg-cream)] border-[var(--border)] ${n.optional ? "border-dashed" : ""}`
            }`}
          >
            {n.done && !n.partial ? <Check className="w-3.5 h-3.5" /> : n.partial ? <span className="w-2.5 h-2.5 rounded-full bg-[var(--terracotta)]" /> : null}
          </span>
          <span className={`mt-2 text-[11px] leading-tight px-1 ${n.done || n.id === nextId ? "text-[var(--ink-forest)] font-medium" : "text-[var(--muted-fg)]"}`}>
            {n.short ? <><span className="sm:hidden">{n.short}</span><span className="hidden sm:inline">{n.label}</span></> : n.label}
          </span>
          {n.sub && <span className="text-[10px] text-[var(--muted-fg)] mt-0.5">{n.sub}</span>}
        </li>
      ))}
    </ol>
  );
}

function SupplierStep({ order, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(order.supplier_note || "");
  const [saving, setSaving] = useState(false);
  const step = orderStep(order);

  const save = async (ordered) => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/orders/${order.id}/supplier`, { ordered, note });
      onUpdated(r.data);
      setEditing(false);
      toast.success(ordered ? "Noted — waiting on the supplier" : "Supplier step cleared");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 space-y-3" data-testid="supplier-form">
        <label className="block">
          <span className="text-xs text-[var(--muted-fg)]">Which supplier, and anything to remember (optional)</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Simply Earth, ordered Oct 31 — their order #12345" className={`${INPUT} mt-1`} data-testid="supplier-note-input" />
        </label>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => save(true)} disabled={saving} className={BTN_DARK} data-testid="supplier-save-btn"><Store className="w-4 h-4" /> Save</button>
          <button onClick={() => { setEditing(false); setNote(order.supplier_note || ""); }} className="text-xs text-[var(--muted-fg)] underline" data-testid="supplier-cancel-btn">Cancel</button>
        </div>
      </div>
    );
  }

  if (order.supplier_ordered_at) {
    return (
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 flex items-start gap-3" data-testid="supplier-card">
        <Store className="w-4 h-4 text-[var(--ink-forest)] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--ink-forest)] font-medium">Ordered from supplier · {shortDate(order.supplier_ordered_at)}</p>
          {order.supplier_note && <p className="text-sm text-[var(--muted-fg)] mt-0.5 whitespace-pre-line" data-testid="supplier-note-text">{order.supplier_note}</p>}
        </div>
        <button onClick={() => setEditing(true)} className="text-xs text-[var(--terracotta)] underline" data-testid="supplier-edit-btn">Edit</button>
        <button onClick={() => save(false)} disabled={saving} className="text-xs text-[var(--muted-fg)] underline" data-testid="supplier-undo-btn">Undo</button>
      </div>
    );
  }

  if (!["new", "partly"].includes(step)) return null;
  return (
    <button onClick={() => setEditing(true)} className={BTN_SOFT} data-testid="supplier-ordered-btn">
      <Store className="w-4 h-4" /> Ordered from supplier
    </button>
  );
}

function PackageCard({ order, shipment, index, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const first = (order.customer_name || "the customer").split(" ")[0];

  const sendEmail = async () => {
    if (!window.confirm(`Email ${first} that this package is on its way?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/shipments/${shipment.id}/email`);
      onUpdated(r.data);
      toast.success(`${first} has been emailed`);
    } catch (e) {
      toast.error(errMsg(e, "Could not send the email"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this package? Use this if the tracking details were entered by mistake.")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/orders/${order.id}/shipments/${shipment.id}`);
      onUpdated(r.data);
      toast.success("Package removed");
    } catch (e) {
      toast.error(errMsg(e, "Could not remove"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 space-y-3" data-testid={`package-card-${shipment.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center shrink-0">
          {shipment.shipped_by === "supplier" ? <Store className="w-4 h-4 text-[var(--terracotta)]" /> : <Home className="w-4 h-4 text-[var(--terracotta)]" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--ink-forest)]">
            Package {index + 1} · {carrierLabel(shipment)}
          </p>
          <p className="text-[11px] text-[var(--muted-fg)]">
            {shipment.shipped_by === "supplier" ? "Supplier is shipping" : "You're shipping"} · added {shortDate(shipment.created_at)}
          </p>
        </div>
        <button onClick={remove} disabled={busy} className="text-[var(--muted-fg)] hover:text-[var(--terracotta)] p-1" aria-label="Remove package" data-testid={`package-remove-${shipment.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        {shipment.tracking_number && <span className="font-mono text-[var(--ink-forest)]" data-testid={`package-tracking-${shipment.id}`}>{shipment.tracking_number}</span>}
        {shipment.tracking_url && (
          <a href={shipment.tracking_url} target="_blank" rel="noreferrer" className="text-[var(--terracotta)] inline-flex items-center gap-1 text-xs underline" data-testid={`package-link-${shipment.id}`}>
            Track it <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <ul className="text-xs text-[var(--muted-fg)] space-y-0.5">
        {shipment.item_indices.map((i) => order.items[i] && (
          <li key={i}>{effectiveQty(order.items[i])} × {order.items[i].name}</li>
        ))}
        {(shipment.swap_ids || []).map((sid) => {
          const sw = (order.swaps || []).find((x) => x.id === sid);
          return sw && <li key={sid} data-testid={`package-swap-${sid}`}>{sw.quantity} × {sw.name} <span className="text-[10px] uppercase tracking-wider">(swap)</span></li>;
        })}
      </ul>
      {shipment.customer_emailed ? (
        <p className="text-[11px] text-[var(--terracotta)] inline-flex items-center gap-1" data-testid={`package-emailed-${shipment.id}`}>
          <Check className="w-3 h-3" /> Customer emailed · {shortDate(shipment.emailed_at)}
        </p>
      ) : order.practice_copy ? (
        <p className="text-[11px] text-[var(--muted-fg)]" data-testid={`package-practice-note-${shipment.id}`}>Practice copy · customer emails are off</p>
      ) : (
        <button onClick={sendEmail} disabled={busy} className={`${BTN_SOFT} !text-xs !px-3 !py-1.5`} data-testid={`package-send-email-${shipment.id}`}>
          <Mail className="w-3.5 h-3.5" /> Send &ldquo;on its way&rdquo; email
        </button>
      )}
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${
        active ? "bg-[var(--ink-forest)] text-[var(--bg-cream)] border-[var(--ink-forest)]" : "bg-[var(--bg-cream)] text-[var(--ink-forest)] border-[var(--border)] hover:border-[var(--ink-forest)]/30"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function AddShipmentForm({ order, onUpdated, onCancel }) {
  const shippedIdx = new Set((order.shipments || []).flatMap((s) => s.item_indices));
  const packageOf = (i) => (order.shipments || []).findIndex((s) => s.item_indices.includes(i)) + 1;
  const [shippedBy, setShippedBy] = useState(order.supplier_ordered_at ? "supplier" : "me");
  const [carrier, setCarrier] = useState("usps");
  const [number, setNumber] = useState("");
  const [url, setUrl] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const swaps = order.swaps || [];
  const shippedSwaps = new Set((order.shipments || []).flatMap((s) => s.swap_ids || []));
  const sendable = order.items.map((_, i) => i).filter((i) => effectiveQty(order.items[i]) > 0);
  const remainingSwaps = swaps.filter((w) => !shippedSwaps.has(w.id)).map((w) => w.id);
  const [picked, setPicked] = useState(() => {
    const remaining = sendable.filter((i) => !shippedIdx.has(i));
    return new Set(remaining.length || remainingSwaps.length ? remaining : sendable);
  });
  const [pickedSwaps, setPickedSwaps] = useState(() => new Set(remainingSwaps));
  const toggleSwap = (id) => setPickedSwaps((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const [saving, setSaving] = useState(false);
  const first = (order.customer_name || "the customer").split(" ")[0];
  const preview = carrier !== "other" ? buildTrackingUrl(carrier, number) : "";

  const onNumber = (v) => {
    setNumber(v);
    if (cleanTracking(v).startsWith("1Z")) setCarrier("ups");
  };

  const toggle = (i) => setPicked((p) => {
    const n = new Set(p);
    n.has(i) ? n.delete(i) : n.add(i);
    return n;
  });

  const save = async (emailCustomer) => {
    if (carrier === "other" ? !/^https?:\/\//i.test(url.trim()) : !cleanTracking(number)) {
      toast.error(carrier === "other" ? "Paste the full tracking link (it starts with http)" : "Add the tracking number");
      return;
    }
    if (!picked.size && !pickedSwaps.size) {
      toast.error("Tick at least one item in this package");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/shipments`, {
        shipped_by: shippedBy,
        carrier,
        tracking_number: number,
        tracking_url: carrier === "other" ? url : "",
        carrier_name: carrier === "other" ? carrierName : "",
        item_indices: [...picked].sort((a, b) => a - b),
        swap_ids: [...pickedSwaps],
        email_customer: emailCustomer,
      });
      onUpdated(r.data.order);
      if (r.data.email_skipped === "practice_copy") {
        toast.success("Package saved. No email sent, because this is a practice copy");
      } else if (emailCustomer && !r.data.email_sent) {
        toast.error("Package saved, but the email couldn't be sent. Use \u201cSend on its way email\u201d to try again.");
      } else {
        toast.success(emailCustomer ? `Package saved and ${first} has been emailed` : "Package saved");
      }
      onCancel();
    } catch (e) {
      toast.error(errMsg(e, "Could not save the package"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border-2 border-[var(--terracotta)]/30 rounded-xl p-5 space-y-5" data-testid="add-shipment-form">
      <div>
        <p className="eyebrow text-[var(--terracotta)]">New package</p>
        <p className="font-serif text-xl text-[var(--ink-forest)]">On its way to {first}</p>
      </div>

      <div>
        <p className="text-xs text-[var(--muted-fg)] mb-2">Who's sending it?</p>
        <div className="flex gap-2 flex-wrap">
          <Pill active={shippedBy === "me"} onClick={() => setShippedBy("me")} testid="ship-by-me">I'm shipping it</Pill>
          <Pill active={shippedBy === "supplier"} onClick={() => setShippedBy("supplier")} testid="ship-by-supplier">The supplier is shipping it</Pill>
        </div>
      </div>

      <div>
        <p className="text-xs text-[var(--muted-fg)] mb-2">Carrier</p>
        <div className="flex gap-2 flex-wrap">
          {CARRIERS.map((c) => (
            <Pill key={c.id} active={carrier === c.id} onClick={() => setCarrier(c.id)} testid={`carrier-${c.id}`}>{c.label}</Pill>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs text-[var(--muted-fg)]">Tracking number{carrier === "other" ? " (optional)" : ""}</span>
        <input value={number} onChange={(e) => onNumber(e.target.value)} placeholder={carrier === "ups" ? "1Z…" : "Paste the tracking number"} className={`${INPUT} mt-1 font-mono`} data-testid="ship-tracking-number" />
        <span className="block text-[11px] text-[var(--muted-fg)] mt-1.5 leading-relaxed" data-testid="ship-tracking-help">
          {shippedBy === "supplier"
            ? "Look in the supplier's \u201cyour order has shipped\u201d email, or on your order page on their website."
            : "It's the long number under the barcode on your shipping label or post office receipt. UPS numbers start with 1Z."}
        </span>
      </label>

      {carrier === "other" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-xs text-[var(--muted-fg)]">Tracking link</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className={`${INPUT} mt-1`} data-testid="ship-tracking-url" />
            <span className="block text-[11px] text-[var(--muted-fg)] mt-1.5">Copy the &ldquo;track package&rdquo; link from the supplier's email and paste it here.</span>
          </label>
          <label className="block">
            <span className="text-xs text-[var(--muted-fg)]">Carrier name (optional)</span>
            <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} placeholder="e.g. DHL" className={`${INPUT} mt-1`} data-testid="ship-carrier-name" />
          </label>
        </div>
      )}

      {preview && (
        <a href={preview} target="_blank" rel="noreferrer" className="text-xs text-[var(--terracotta)] underline inline-flex items-center gap-1" data-testid="ship-tracking-preview">
          {first} will get this tracking link <ExternalLink className="w-3 h-3" />
        </a>
      )}

      <div>
        <p className="text-xs text-[var(--muted-fg)] mb-2">What's in this package?</p>
        <div className="space-y-1.5">
          {order.items.map((it, i) => {
            const qty = effectiveQty(it);
            return (
              <label key={i} className={`flex items-center gap-3 text-sm text-[var(--ink-forest)] ${qty > 0 ? "cursor-pointer" : "opacity-50"}`}>
                <input type="checkbox" checked={picked.has(i)} disabled={qty <= 0} onChange={() => toggle(i)} className="w-4 h-4 accent-[var(--terracotta)]" data-testid={`ship-item-${i}`} />
                <span className="flex-1">{qty} × {it.name}{isShortItem(it) && <span className="text-[10px] text-[var(--terracotta)] ml-1.5">(of {it.quantity} ordered)</span>}</span>
                {qty <= 0 ? <span className="text-[10px] text-[var(--muted-fg)]">not sending</span>
                  : shippedIdx.has(i) && <span className="text-[10px] text-[var(--muted-fg)]">already in package {packageOf(i)}</span>}
              </label>
            );
          })}
          {swaps.map((w) => (
            <label key={w.id} className="flex items-center gap-3 text-sm text-[var(--ink-forest)] cursor-pointer">
              <input type="checkbox" checked={pickedSwaps.has(w.id)} onChange={() => toggleSwap(w.id)} className="w-4 h-4 accent-[var(--terracotta)]" data-testid={`ship-swap-${w.id}`} />
              <span className="flex-1">{w.quantity} × {w.name} <span className="text-[10px] uppercase tracking-wider text-[var(--muted-fg)]">swap</span></span>
              {shippedSwaps.has(w.id) && <span className="text-[10px] text-[var(--muted-fg)]">already in a package</span>}
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap pt-1">
        <button onClick={() => save(true)} disabled={saving} className={BTN_PRIMARY} data-testid="ship-save-email-btn"><Send className="w-4 h-4" /> Save &amp; email customer</button>
        <button onClick={() => save(false)} disabled={saving} className={BTN_SOFT} data-testid="ship-save-btn">Save without email</button>
        <button onClick={onCancel} className="text-xs text-[var(--muted-fg)] underline ml-1" data-testid="ship-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

export default function OrderFulfillment({ order, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const step = orderStep(order);
  const first = (order.customer_name || "the customer").split(" ")[0];
  const shipments = order.shipments || [];

  if (["unpaid", "cancelled", "expired", "refunded"].includes(step)) {
    return (
      <p className="text-sm text-[var(--muted-fg)] bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4" data-testid="fulfillment-locked">
        {step === "unpaid" ? "This order hasn't been paid yet — shipping tools appear once payment comes through." : "This order is closed."}
      </p>
    );
  }

  const markDelivered = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/orders/${order.id}/status`, { status: "delivered" });
      onUpdated(r.data);
      toast.success("Marked as delivered");
    } catch (e) {
      toast.error(errMsg(e, "Could not update"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="order-fulfillment">
      <div>
        <p className="eyebrow mb-3">Getting it to {first}</p>
        <StepPath order={order} />
      </div>

      <SupplierStep key={`${order.id}-${order.supplier_note || ""}`} order={order} onUpdated={onUpdated} />

      {shipments.map((s, i) => (
        <PackageCard key={s.id} order={order} shipment={s} index={i} onUpdated={onUpdated} />
      ))}

      {adding ? (
        <AddShipmentForm order={order} onUpdated={onUpdated} onCancel={() => setAdding(false)} />
      ) : (
        <div className="flex gap-2 flex-wrap">
          {step !== "delivered" && (
            <button onClick={() => setAdding(true)} className={["shipped"].includes(step) ? BTN_SOFT : BTN_DARK} data-testid="order-add-shipment-btn">
              <Truck className="w-4 h-4" /> {shipments.length || step === "shipped" ? "Add another package" : "Add shipment"}
            </button>
          )}
          {step === "shipped" && (
            <button onClick={markDelivered} disabled={busy} className={BTN_DARK} data-testid="order-mark-delivered-btn">
              <PackageCheck className="w-4 h-4" /> Mark as delivered
            </button>
          )}
          {step === "delivered" && (
            <p className="text-sm text-[var(--ink-forest)] inline-flex items-center gap-2" data-testid="order-delivered-note">
              <PackageCheck className="w-4 h-4 text-[var(--terracotta)]" /> Delivered{order.delivered_at ? ` · ${shortDate(order.delivered_at)}` : ""}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
